// DeleteItemDialog — confirmation prompt for removing an explorer folder or
// leaf row. Pass `recursive` so folder delete removes descendants, and
// `itemNote` for whatever else a leaf delete takes with it (a stored file, a
// revision history).

import { useState } from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '../ui/alert-dialog'
import type { ExplorerItemKind } from './item-kind'
import { readMutationError } from './mutation-error'

export type DeleteItemKind = ExplorerItemKind

export interface DeleteItemDialogProps {
  open: boolean
  kind: DeleteItemKind
  /** Override for the folder noun in copy. Defaults to "folder". */
  folderNoun?: string
  /** Display name of the row being deleted. */
  name: string
  /** Path of the row being deleted (rendered for disambiguation). */
  path: string
  /**
   * Number of leaf rows + descendant folders that share this folder's path
   * prefix. Only meaningful for folder deletes; leave unset for leaf rows.
   */
  descendantCount?: number
  /**
   * When true, folder delete removes descendants.
   */
  recursive?: boolean
  /**
   * Extra clause appended to a leaf delete, e.g. "and remove its stored
   * file". Rendered right after the path, so phrase it as a continuation.
   */
  itemNote?: string
  onClose: () => void
  onConfirm: () => Promise<void>
}

const DeleteItemDialog = ({
  open,
  kind,
  folderNoun = 'folder',
  name,
  path,
  descendantCount = 0,
  recursive = false,
  itemNote,
  onClose,
  onConfirm
}: DeleteItemDialogProps) => {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConfirm = async () => {
    setSubmitting(true)
    setError(null)
    try {
      await onConfirm()
      onClose()
    } catch (err) {
      setError(readMutationError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  const noun = kind === 'folder' ? folderNoun : kind

  return (
    <AlertDialog open={open} onOpenChange={(next) => (!next ? handleClose() : undefined)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {noun}?</AlertDialogTitle>
          <AlertDialogDescription>
            {kind === 'folder' && recursive && descendantCount > 0 ? (
              <span>
                This will permanently delete <strong>{name}</strong> at{' '}
                <code className="font-mono text-12">/{path}</code> and {descendantCount} item
                {descendantCount === 1 ? '' : 's'} inside it.
              </span>
            ) : (
              <span>
                This will permanently delete <strong>{name}</strong> at{' '}
                <code className="font-mono text-12">/{path}</code>
                {kind !== 'folder' && itemNote ? ` ${itemNote}` : ''}.
              </span>
            )}
            {kind === 'folder' && !recursive && descendantCount > 0 ? (
              <span className="mt-2 block text-st-down">
                {descendantCount} item{descendantCount === 1 ? '' : 's'} below this folder will not
                be deleted; they will remain in the tree under the same path.
              </span>
            ) : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error ? (
          <div className="rounded-md bg-st-down-bg p-2.5 text-12 text-st-down" role="alert">
            {error}
          </div>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={submitting}
            onClick={(event) => {
              event.preventDefault()
              void handleConfirm()
            }}
          >
            {submitting ? 'Deleting…' : 'Delete'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default DeleteItemDialog
